import { computed, observable } from "mobx";
import React from "react";
import { Utils } from "../Utils";
import { NodeCollectionStore } from "./NodeCollectionStore";
import { HALF, NODE_SIDE_LENGTH, ORIGIN } from "../Constants";

/**
 * This enum defines the various types of nodes that can exist in the application.
 * This is used throughout the program to distinguish node behavior and rendering.
 */
export enum StoreType {
    Text,
    Video,
    Image,
    Web,
    Collection,
    Audio
}

/**
 * This class is the base store for all nodes in the application.
 * It manages shared properties such as position, size, and links to other nodes.
 * This class also encapsulates logic for calculating world position and styling.
 */
export class NodeStore {
    public Id: string = Utils.GenerateGuid();

    public type: StoreType | null = null;

    @observable
    public x: number = ORIGIN;

    @observable
    public y: number = ORIGIN;

    @observable
    public width: number = NODE_SIDE_LENGTH;

    @observable
    public height: number = NODE_SIDE_LENGTH;

    @observable
    public links: string[] = [];
    prevWidth?: number;
    prevHeight?: number;

    public parent?: NodeCollectionStore;

    /**
     * This method computes the inline CSS styles for positioning and sizing the node.
     */
    @computed
    public get style(): React.CSSProperties {
        return {
            position: "absolute",
            transform: `translate(${this.x}px, ${this.y}px)`,
            width: this.width + "px",
            height: this.height + "px",
        };
    }

    /**
     * This method calculates the world position of the node by getting the position of all
     * parent nodes and allowing other classes to know where this node is in the canvas space.
     * @returns The world position of the nested node as x and y coordinates.
     */
    public getWorldPosition(): { x: number; y: number } {
        let x = this.x;
        let y = this.y;

        let current = this.parent;
        while (current) {
            x += current.x;
            y += current.y;
            current = current.parent;
        }

        return { x, y };
    }

    /**
     * This method computers the center point of the node in world coordinates.
     * The calculation is based on the node's world position and its dimensions.
     * @returns The center point of the node in world coordinates.
     */
    public getWorldCenter(): { x: number; y: number } {
        const pos = this.getWorldPosition();
        return {
            x: pos.x + this.width * HALF,
            y: pos.y + this.height * HALF,
        };
    }
}